import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ArrowLeftIcon, BoltIcon, ArrowDownTrayIcon, PlayCircleIcon,
  CurrencyDollarIcon, BanknotesIcon, ReceiptPercentIcon, ShieldCheckIcon,
  BuildingOffice2Icon, EyeIcon,
} from '@heroicons/react/24/outline';
import { payrollApi, payrollEngineApi } from '../../../services/api';
import {
  PageHeader, StatCard, Spinner, EmptyState, Modal,
} from '../../../components/shared/index';
import StubDetail from './StubDetail';

const ACH_FIELDS = [
  { key: 'destination_routing', label: 'Destination routing #', placeholder: '091000019',  type: 'routing' },
  { key: 'destination_name',    label: 'Destination bank name', placeholder: 'WELLS FARGO', type: 'text' },
  { key: 'origin_id',           label: 'Origin ID',             placeholder: '1234567890',  type: 'text' },
  { key: 'odfi_routing',        label: 'ODFI routing #',        placeholder: '121000248',   type: 'routing' },
  { key: 'company_name',        label: 'Company name',          placeholder: 'CALIBZA INC', type: 'text' },
  { key: 'company_id',          label: 'Company ID',            placeholder: '9876543210',  type: 'text' },
];

const ACH_STORAGE_KEY = 'calibza_ach_originator';

function fmtMoney(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);
}

function AchModal({ open, onClose, onSubmit, pending }) {
  const [form, setForm] = useState(() => {
    try {
      const stored = localStorage.getItem(ACH_STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch { /* ignore */ }
    return Object.fromEntries(ACH_FIELDS.map(f => [f.key, '']));
  });

  useEffect(() => {
    if (open) {
      try {
        const stored = localStorage.getItem(ACH_STORAGE_KEY);
        if (stored) setForm((f) => ({ ...f, ...JSON.parse(stored) }));
      } catch { /* ignore */ }
    }
  }, [open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const valid = ACH_FIELDS.every(f => {
    const v = String(form[f.key] || '').trim();
    if (!v) return false;
    if (f.type === 'routing') return /^\d{9}$/.test(v);
    return true;
  });

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    try { localStorage.setItem(ACH_STORAGE_KEY, JSON.stringify(form)); } catch { /* ignore */ }
    onSubmit(form);
  };

  return (
    <Modal open={open} onClose={onClose} title="Generate NACHA / ACH file" size="md">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-xs text-gray-500">
          All fields are required. Values are saved locally for next time.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ACH_FIELDS.map((f) => {
            const v = form[f.key] || '';
            const invalid = f.type === 'routing' && v && !/^\d{9}$/.test(v);
            return (
              <div key={f.key} className={f.key === 'company_name' ? 'md:col-span-2' : ''}>
                <label className="label">{f.label} *</label>
                <input
                  required
                  className={clsx('input', invalid && 'border-red-400')}
                  value={v}
                  maxLength={f.type === 'routing' ? 9 : undefined}
                  inputMode={f.type === 'routing' ? 'numeric' : undefined}
                  onChange={(e) => {
                    const val = f.type === 'routing' ? e.target.value.replace(/\D/g, '') : e.target.value;
                    set(f.key, val);
                  }}
                  placeholder={f.placeholder}
                />
                {invalid && <p className="text-xs text-red-600 mt-1">Must be 9 digits.</p>}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            type="submit"
            disabled={!valid || pending}
            className={clsx('btn-primary', (!valid || pending) && 'opacity-50')}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {pending ? 'Generating…' : 'Download ACH'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function RunEngineModal({ open, onClose, onConfirm, pending }) {
  const [suta, setSuta] = useState('0.027');

  const submit = (e) => {
    e.preventDefault();
    const num = Number(suta);
    if (!Number.isFinite(num) || num < 0 || num > 1) {
      toast.error('SUTA rate must be between 0 and 1 (e.g. 0.027)');
      return;
    }
    onConfirm({ suta_rate: num });
  };

  return (
    <Modal open={open} onClose={onClose} title="Run payroll engine" size="sm">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-gray-600">
          This will compute gross → net for every active employee and create/overwrite their pay stubs for this period.
        </p>
        <div>
          <label className="label">SUTA rate (state unemployment)</label>
          <input
            type="number" step="0.001" min="0" max="1"
            className="input"
            value={suta}
            onChange={(e) => setSuta(e.target.value)}
          />
          <p className="text-xs text-gray-500 mt-1">Default <code>0.027</code> (2.7%). Varies by state and employer history.</p>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            type="submit"
            disabled={pending}
            className={clsx('btn-primary', pending && 'opacity-50')}
          >
            <BoltIcon className="w-4 h-4" />
            {pending ? 'Running…' : 'Run engine'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function RunEngine() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [achOpen, setAchOpen]       = useState(false);
  const [runOpen, setRunOpen]       = useState(false);
  const [stubId,  setStubId]        = useState(null);

  const { data: period, isLoading: loadingPeriod } = useQuery({
    queryKey: ['payroll', 'period', id],
    queryFn:  () => payrollApi.period(id).then(r => r.data),
    enabled:  !!id,
  });

  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ['payroll', 'summary', id],
    queryFn:  () => payrollEngineApi.summary(id).then(r => r.data),
    enabled:  !!id,
  });

  const runEngine = useMutation({
    mutationFn: (data) => payrollEngineApi.runEngine(id, data),
    onSuccess: () => {
      toast.success('Engine run complete');
      setRunOpen(false);
      qc.invalidateQueries({ queryKey: ['payroll', 'summary', id] });
      qc.invalidateQueries({ queryKey: ['payroll', 'period', id] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Engine run failed'),
  });

  const downloadAch = useMutation({
    mutationFn: (originator) => payrollEngineApi.downloadAch(id, originator),
    onSuccess: (res) => {
      try {
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `payroll-${id}-ach.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success('ACH file downloaded');
        setAchOpen(false);
      } catch {
        toast.error('Download failed');
      }
    },
    onError: (e) => toast.error(e.response?.data?.message || 'ACH generation failed'),
  });

  if (loadingPeriod || loadingSummary) {
    return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  }

  if (!period) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <EmptyState
          title="Period not found"
          description="This payroll period may have been deleted."
          action={<button onClick={() => navigate('/app/payroll')} className="btn-primary">Back to payroll</button>}
        />
      </div>
    );
  }

  const totals   = summary?.totals || {};
  const employer = summary?.employer_liability || {};
  const rows     = summary?.rows || summary?.employees || period?.runs || [];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Link
        to={`/app/payroll/${id}`}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
      >
        <ArrowLeftIcon className="w-4 h-4" /> Back to payroll detail
      </Link>

      <PageHeader
        title={`${period.name} · Engine`}
        subtitle={`Run the real payroll engine — W-4, FICA, FIT, SIT, deductions, direct deposit (ACH).`}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setRunOpen(true)}
              disabled={runEngine.isPending}
              className="btn-primary"
            >
              <BoltIcon className="w-4 h-4" />
              {runEngine.isPending ? 'Running…' : 'Run engine'}
            </button>
            <button
              onClick={() => setAchOpen(true)}
              disabled={downloadAch.isPending || !rows.length}
              className="btn-secondary"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              {downloadAch.isPending ? 'Generating…' : 'Download ACH'}
            </button>
          </div>
        }
      />

      {/* Progress banner while running */}
      {runEngine.isPending && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-5 text-sm text-blue-800">
          <Spinner size="sm" />
          Computing taxes, deductions and direct deposit splits for all employees…
        </div>
      )}

      {/* Stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
        <StatCard title="Gross"            value={fmtMoney(totals.gross)}          color="blue"   icon={CurrencyDollarIcon} />
        <StatCard title="Pre-tax deds"     value={fmtMoney(totals.pre_tax_total)}  color="purple" icon={BanknotesIcon} />
        <StatCard title="Federal tax"      value={fmtMoney(totals.federal_tax)}    color="red"    icon={ReceiptPercentIcon} />
        <StatCard title="State tax"        value={fmtMoney(totals.state_tax)}      color="amber"  icon={ReceiptPercentIcon} />
        <StatCard title="FICA (SS+MC)"     value={fmtMoney(totals.fica)}           color="red"    icon={ShieldCheckIcon} />
        <StatCard title="Net"              value={fmtMoney(totals.net)}            color="green"  icon={BanknotesIcon} />
        <StatCard title="Employer burden"  value={fmtMoney(employer.total)}        color="amber"  icon={BuildingOffice2Icon} />
      </div>

      {/* Employer liability breakdown */}
      {Object.keys(employer).length > 0 && (
        <div className="card p-4 mb-6">
          <h3 className="section-title mb-3">Employer liability breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500 uppercase">ER Social Security</p>
              <p className="font-semibold text-gray-900">{fmtMoney(employer.employer_ss)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">ER Medicare</p>
              <p className="font-semibold text-gray-900">{fmtMoney(employer.employer_medicare)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">FUTA</p>
              <p className="font-semibold text-gray-900">{fmtMoney(employer.futa)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">SUTA</p>
              <p className="font-semibold text-gray-900">{fmtMoney(employer.suta)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase">Employer match</p>
              <p className="font-semibold text-gray-900">{fmtMoney(employer.employer_match)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Per-employee */}
      <div className="card p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="section-title">Per-employee breakdown</h2>
          {!rows.length && (
            <span className="text-xs text-gray-500">Run the engine to populate.</span>
          )}
        </div>

        {!rows.length ? (
          <EmptyState
            title="No stubs yet"
            description="Click Run engine above to compute gross → net for every active employee."
            action={
              <button onClick={() => setRunOpen(true)} className="btn-primary">
                <PlayCircleIcon className="w-4 h-4" /> Run engine
              </button>
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {['Employee', 'Gross', 'Pre-tax', 'FIT', 'SIT', 'FICA', 'Net', ''].map((h) => (
                    <th key={h} className="th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {rows.map((r) => {
                  const name  = r.employee?.name || r.name || '—';
                  const email = r.employee?.email || r.email;
                  const rowStubId = r.pay_stub_id || r.stub_id || r.id;
                  return (
                    <tr key={rowStubId || name} className="tr-hover">
                      <td className="td">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">
                            {name?.[0]?.toUpperCase() ?? '?'}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{name}</div>
                            {email && <div className="text-xs text-gray-500">{email}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="td text-sm">{fmtMoney(r.gross)}</td>
                      <td className="td text-sm">{fmtMoney(r.pre_tax_total)}</td>
                      <td className="td text-sm">{fmtMoney(r.federal_tax)}</td>
                      <td className="td text-sm">{fmtMoney(r.state_tax)}</td>
                      <td className="td text-sm">{fmtMoney(r.fica)}</td>
                      <td className="td text-sm font-semibold text-green-700">{fmtMoney(r.net)}</td>
                      <td className="td">
                        {rowStubId && (
                          <button
                            onClick={() => setStubId(rowStubId)}
                            className="btn-secondary btn-sm"
                          >
                            <EyeIcon className="w-4 h-4" /> View stub
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RunEngineModal
        open={runOpen}
        onClose={() => setRunOpen(false)}
        onConfirm={(data) => runEngine.mutate(data)}
        pending={runEngine.isPending}
      />

      <AchModal
        open={achOpen}
        onClose={() => setAchOpen(false)}
        onSubmit={(data) => downloadAch.mutate(data)}
        pending={downloadAch.isPending}
      />

      <Modal open={!!stubId} onClose={() => setStubId(null)} title="Pay stub detail" size="lg">
        {stubId && <StubDetail stubId={stubId} />}
      </Modal>
    </div>
  );
}
